import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { ChevronDown, Truck, Plus, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

export default function TruckSwitcher({ activeBizId, onSwitch }) {
  const [businesses, setBusinesses] = useState([]);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    axios.get(`${API}/vendor/businesses`).then((res) => {
      setBusinesses(res.data.businesses || []);
    }).catch(() => {}).catch(() => {});
  }, [activeBizId]);

  const handleSwitch = async (bizId) => {
    if (bizId === activeBizId) { setOpen(false); return; }
    setSwitching(true);
    try {
      const res = await axios.post(`${API}/vendor/switch-business`, { business_id: bizId });
      toast.success(`Switched to ${res.data.truck_name}`);
      onSwitch?.(bizId);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to switch");
    } finally {
      setSwitching(false);
    }
  };

  // Only show switcher if vendor has more than 1 truck
  if (businesses.length <= 1 && !showAddModal) {
    return (
      <button
        onClick={() => setShowAddModal(true)}
        className="text-[10px] text-[#F59E0B]/60 hover:text-[#F59E0B] font-medium flex items-center gap-0.5 transition"
        data-testid="add-first-truck-btn"
      >
        <Plus className="w-3 h-3" /> Add Truck
      </button>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-[10px] text-white/40 hover:text-white/60 transition"
        data-testid="truck-switcher-btn"
      >
        {businesses.length} trucks <ChevronDown className={`w-3 h-3 transition ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/60 flex items-end justify-center"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ y: 200 }}
              animate={{ y: 0 }}
              exit={{ y: 200 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-[#1C1C1E] rounded-t-2xl border-t border-white/10 p-4 pb-8"
              data-testid="truck-switcher-sheet"
            >
              <div className="w-8 h-1 bg-white/10 rounded-full mx-auto mb-4" />
              <p className="text-sm font-bold text-white/80 mb-3">Your Trucks</p>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {businesses.map((biz) => (
                  <button
                    key={biz.business_id}
                    onClick={() => handleSwitch(biz.business_id)}
                    disabled={switching}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition ${
                      biz.business_id === activeBizId
                        ? "bg-[#F59E0B]/10 border-[#F59E0B]/30"
                        : "bg-[#2C2C2E] border-white/5 hover:border-[#F59E0B]/20"
                    }`}
                    data-testid={`switch-truck-${biz.business_id}`}
                  >
                    {biz.logo_url ? (
                      <img src={biz.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-[#1C1C1E] flex items-center justify-center">
                        <Truck className="w-4 h-4 text-[#F59E0B]" />
                      </div>
                    )}
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-xs font-bold text-white/80 truncate">{biz.name}</p>
                      <p className="text-[9px] text-white/30">{biz.city}, {biz.state}</p>
                    </div>
                    {biz.business_id === activeBizId && (
                      <Check className="w-4 h-4 text-[#F59E0B] shrink-0" />
                    )}
                    {biz.live_session?.status === "live" && (
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
                    )}
                  </button>
                ))}
              </div>

              <button
                onClick={() => { setOpen(false); setShowAddModal(true); }}
                className="w-full mt-3 py-2.5 rounded-xl border border-dashed border-white/10 text-white/40 text-xs font-semibold hover:border-[#F59E0B]/30 hover:text-[#F59E0B] transition flex items-center justify-center gap-1.5"
                data-testid="add-truck-from-switcher"
              >
                <Plus className="w-3.5 h-3.5" /> Add Another Truck
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Truck Modal */}
      <AddTruckModal show={showAddModal} onClose={() => setShowAddModal(false)} onAdded={(biz) => {
        setBusinesses((prev) => [...prev, biz]);
        setShowAddModal(false);
        toast.success(`${biz.truck_name} added!`);
      }} />
    </>
  );
}

function AddTruckModal({ show, onClose, onAdded }) {
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("CA");
  const [saving, setSaving] = useState(false);

  if (!show) return null;

  const handleAdd = async () => {
    if (!name.trim() || !city.trim()) {
      toast.error("Truck name and city are required");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/vendor/add-truck`, {
        truck_name: name.trim(),
        city: city.trim(),
        state: state.trim(),
      });
      onAdded?.(res.data);
      setName(""); setCity("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add truck");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-6" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#1C1C1E] rounded-2xl p-5 w-full max-w-sm border border-white/10"
        data-testid="add-truck-modal"
      >
        <p className="text-sm font-bold text-white mb-4 flex items-center gap-2">
          <Plus className="w-4 h-4 text-[#F59E0B]" /> Add Another Truck
        </p>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-white/40 block mb-1">Truck Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Taco Express"
              className="w-full bg-[#2C2C2E] text-white text-sm px-3 py-2.5 rounded-lg border border-white/10 focus:border-[#F59E0B]/50 outline-none"
              data-testid="add-truck-name"
            />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-[10px] text-white/40 block mb-1">City</label>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Sacramento"
                className="w-full bg-[#2C2C2E] text-white text-sm px-3 py-2.5 rounded-lg border border-white/10 focus:border-[#F59E0B]/50 outline-none"
                data-testid="add-truck-city"
              />
            </div>
            <div className="w-20">
              <label className="text-[10px] text-white/40 block mb-1">State</label>
              <input
                value={state}
                onChange={(e) => setState(e.target.value)}
                maxLength={2}
                className="w-full bg-[#2C2C2E] text-white text-sm px-3 py-2.5 rounded-lg border border-white/10 focus:border-[#F59E0B]/50 outline-none uppercase"
                data-testid="add-truck-state"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl bg-[#2C2C2E] text-white/40 text-xs font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-[#F59E0B] text-[#1C1C1E] text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50"
            data-testid="confirm-add-truck"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Add Truck
          </button>
        </div>
      </motion.div>
    </div>
  );
}
